/**
 * Card for a session.
 */
import * as React from 'react';
import { Building2, Calendar, Clock, Repeat2, Users } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { SESSION_RECURRENCE_CONFIG } from '@/components/sessions/session-configs';
import { SessionActions } from './SessionActions';
import { SessionTypeBadge } from './SessionTypeBadge';
import { formatSessionGroupCountLabel, formatSessionTime } from './session-table-utils';
import { useSessionItem, type SessionItemProps } from '@/hooks/useSessionItem';

/** Card for a session. */
export const SessionCard: React.FC<SessionItemProps> = (props) => {
  const { session, onView } = props;
  const state = useSessionItem(props);
  const StatusIcon = state.statusConfig?.icon;
  const groupCountLabel = state.groupDisplay
    ? formatSessionGroupCountLabel(
        state.groupDisplay.groupCount,
        state.groupDisplay.activeGroupCount,
        state.groupDisplay.groupCount - state.groupDisplay.activeGroupCount,
      )
    : '';

  return (
    <TableCard onClick={() => state.itemId && onView?.(state.itemId)}>
      <div className="data-card-header">
        {state.statusConfig && (
          <LabelBadge
            label={state.statusConfig.label}
            icon={
              state.statusConfig.isInProgress ? <span className="session-dot-pulse" /> : StatusIcon
            }
            variant={state.statusConfig.badgeVariant}
            color={state.statusConfig.badgeColor}
            size="sm"
          />
        )}
      </div>

      <div className="data-card-content" style={{ overflow: 'hidden' }}>
        <div className="flex items-center gap-2 mb-1">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-foreground text-sm">
              {format(state.sessionDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </span>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3 text-[var(--color-1-nuance-1)]" />
              <span>
                {formatSessionTime(state.startTime)} – {formatSessionTime(state.endTime)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2 mb-2 min-h-0">
          <Building2
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            style={{ color: 'var(--color-1-nuance-1)' }}
          />
          <span className="text-sm text-foreground line-clamp-2 overflow-hidden">
            {state.establishmentLabel}
          </span>
        </div>
        <div className="mt-auto flex flex-col items-center gap-1.5">
          <SessionTypeBadge type={state.type} size="sm" />
          {state.groupDisplay ? (
            <LabelBadge
              label={groupCountLabel}
              icon={Users}
              variant="outlined"
              color="var(--color-1-nuance-1)"
              size="sm"
            />
          ) : null}
          {session?.mode === 'recurring' && session.recurrenceType && (
            <LabelBadge
              label={
                SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.label || session.recurrenceType
              }
              icon={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.icon ?? Repeat2}
              variant={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeVariant ?? 'ghost'}
              color={
                SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeColor ??
                'var(--color-1-nuance-1)'
              }
              size="sm"
            />
          )}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <SessionActions {...props} state={state} stopPropagation />
        </div>
      </div>
    </TableCard>
  );
};
